import { gsap } from "gsap";
import { Container, Text } from "pixi.js";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { EventsManager } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { AbstractWinScreen } from "../../../abstracts/modules/screens/AbstractWinScreen";
import { LevelName, TimelineLabels, WinType } from "../../../abstracts/types/enums";
import {
    IBetWayWinData,
    ITotalWinDecoratorData,
    IWinLevel,
} from "../../../abstracts/types/interfaces";
import { fitDisplayObjectToWidth } from "../../../abstracts/utils/GraphicUtils";
import { getTotalFromArrayByPropertyName } from "../../../abstracts/utils/JSUtils";
import { playSound } from "../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../Apocalypse";
import { ApocalypseTTF } from "../../assetsLoader/ApocalypseAssetsLoader";
import { ApocalypseSoundNames } from "../../assetsLoader/ApocalypseAssetsNames";
import { symbolConfig } from "../../config/ApocalypseConfig";
import { ANIMATE_BG_BILLBOARDS } from "../../events/ApocalypseEvents";
import ApocalypseReel from "../../modules/Reels/normal/ApocalypseReel";
import { ApocalypseSymbol } from "../../modules/Reels/symbols/ApocalypseSymbol";
import { ApocalypseWildSymbol } from "../../modules/Reels/symbols/ApocalypseWildSymbol";
import { ApocalypseXNudgeSymbol } from "../../modules/Reels/symbols/ApocalypseXNudgeSymbol";
import { ApocalypseSpecialSymbolNames } from "../../types/ApocalypseEnums";
import { waysWinAnimationData } from "../../types/ApocalypseInterfaces";

export const totalWinDecorator = (data: ITotalWinDecoratorData): void => {
    const {
        winLevels = [],
        winAmount = 0,
        timeline = gsap.timeline(),
        winScreen = new AbstractWinScreen(`WinScreen`, new AbstractView()),
        betValue = 1,
        finishTextAnimationDuration = 1.5,
        finishSpineAnimationDuration = 0.6,
        winIsBelowStake = false,
        reels = [],
        winSymbolsPos = [],
        betWayWins = [],
        currentGameMode = AbstractGameMode.NORMAL,
        nextGameMode = AbstractGameMode.NORMAL,
        onDecoratorComplete = () => {},
        onDecoratorStart = () => {},
    } = data;

    if (winAmount === 0) {
        return;
    }

    timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_START);
    onDecoratorStart();

    // animate symbols start
    timeline.addLabel(
        TimelineLabels.START_SYMBOLS_WIN_ANIMATION,
        TimelineLabels.TOTAL_WIN_DECORATOR_START
    );

    const winMultiplier: number = winAmount / betValue;
    let minBigWinIndex: number = winLevels.findIndex(
        (level: IWinLevel) => level.name === LevelName.BIG_WIN_1
    );
    const winLevel: IWinLevel = winLevels.filter(
        (level: IWinLevel) => level.min <= winMultiplier && level.max > winMultiplier
    )[0];

    let winType: WinType = winLevel.winType;

    const waysWon: number =
        currentGameMode === AbstractGameMode.NORMAL || nextGameMode === AbstractGameMode.NORMAL
            ? getTotalFromArrayByPropertyName(betWayWins, `numberOfCombinations`)
            : -1;

    const symbolsAndWaysTimeline = gsap.timeline({
        smoothChildTiming: true,
        autoRemoveChildren: true,
        onStart: () => {
            EventsManager.trigger(ANIMATE_BG_BILLBOARDS, winLevel);
            playSound({ name: ApocalypseSoundNames.WIN_SWEEP });
            if (winType === WinType.BIG || winType === WinType.MAX_CAP) {
                playSound({ name: ApocalypseSoundNames.BIG_WIN_INTRO });
            }
        },
    });

    const symbolsWinTl = gsap.timeline({
        repeat: 1,
    });
    reels.forEach((reel: AbstractReel) => {
        const visibleSymbols = (reel as ApocalypseReel).getAllVisibleStopSymbols();

        if (winSymbolsPos.length > 0) {
            visibleSymbols.forEach((symbol: AbstractSymbol, symbolIndex) => {
                if (winSymbolsPos[reel.reelId][symbolIndex] === false) {
                    symbolsWinTl.add((symbol as ApocalypseSymbol).getNoWinAnimation(), 0);
                } else {
                    if (!winIsBelowStake) {
                        symbolsAndWaysTimeline.add(
                            (symbol as ApocalypseSymbol).getWinAnimation(),
                            0
                        );
                    }
                }
            });
        }
    });

    symbolsAndWaysTimeline.add(symbolsWinTl, 0);

    /////////////
    if (betWayWins.length >= 1 && currentGameMode === AbstractGameMode.NORMAL) {
        const allWaysWinData: waysWinAnimationData[] = [];

        betWayWins.forEach((betWayData: IBetWayWinData) => {
            const wayWinSymbols: AbstractSymbol[] = [];

            reels.forEach((gameReel, reelIndex) => {
                const reel = gameReel as ApocalypseReel;

                reel.getAllVisibleStopSymbols();
                const reelWinSymbols = reel.getWinningSymbols(winSymbolsPos[reelIndex]);

                reelWinSymbols.forEach((symbol) => {
                    const cond1 = reelIndex <= betWayData.length - 1;
                    const cond2 = symbol.name === betWayData.symbol;

                    const cond3 = symbol.name === ApocalypseSpecialSymbolNames.XNUDGE;
                    const cond4 = symbol.name === ApocalypseSpecialSymbolNames.WILD;

                    if (
                        winSymbolsPos[reelIndex][symbol.positionInArray - 1] &&
                        cond1 &&
                        (cond2 || cond3 || cond4)
                    ) {
                        wayWinSymbols.push(symbol as ApocalypseSymbol);
                    }
                });
            });

            if (wayWinSymbols.length > 0) {
                winScreen.show();

                wayWinSymbols.forEach((symbol, symbolIndex) => {
                    if (symbolIndex === wayWinSymbols.length - 1) {
                        const textContainer: Container = new Container();
                        textContainer.name = `waysContainer`;

                        let valueText = new Text(
                            `${(betWayData.amount * betWayData.multiplier).toFixed(2)}`,
                            {
                                fontFamily: ApocalypseTTF.FAMILY,
                                fontSize: `20px`,
                                stroke: `#000000`,
                                strokeThickness: 2,
                                fill: `#00f4da`,
                                letterSpacing: 1,
                                lineJoin: `round`,
                            }
                        );
                        valueText.name = `valueText`;
                        fitDisplayObjectToWidth(valueText, 118);

                        textContainer.addChild(valueText);

                        const waysWonString =
                            betWayData.numberOfCombinations === 1
                                ? Apocalypse.apiPlugin.translations.translate(`Way`)
                                : Apocalypse.apiPlugin.translations.translate(`Ways`);

                        let waysText = new Text(
                            `${betWayData.numberOfCombinations} ${waysWonString}`.toUpperCase(),
                            {
                                fontFamily: ApocalypseTTF.FAMILY,
                                fontSize: `14px`,
                                stroke: `#000000`,
                                strokeThickness: 2,
                                fill: [`#f7c238`, `#c77826`], // dropShadow: true,
                                letterSpacing: 0.5,
                                lineJoin: `round`,
                            }
                        );
                        waysText.name = `waysText`;
                        fitDisplayObjectToWidth(waysText, 118);
                        waysText.y = valueText.height - 1;

                        textContainer.addChild(waysText);

                        if (valueText.width >= waysText.width) {
                            waysText.x = valueText.width / 2 - waysText.width / 2;
                        } else {
                            valueText.x = waysText.width / 2 - valueText.width / 2;
                        }

                        textContainer.pivot.set(textContainer.width / 2, textContainer.height / 2);

                        const symbolGlobalPosition = symbol.getGlobalPosition();
                        const winScreenSymbolLocalPosition =
                            winScreen.toLocal(symbolGlobalPosition);
                        textContainer.x =
                            winScreenSymbolLocalPosition.x + symbolConfig.symbolWidth / 2 + 7;
                        textContainer.y =
                            winScreenSymbolLocalPosition.y + symbolConfig.symbolHeight / 2 + 7;

                        if (symbol instanceof ApocalypseWildSymbol) {
                            if (symbol.size === 4) {
                                textContainer.x = 0;
                                textContainer.y = 44;
                            }
                        } else if (symbol instanceof ApocalypseXNudgeSymbol) {
                            if (symbol.size === 4) {
                                textContainer.y = 46;
                            } else if (symbol.size === 3) {
                                textContainer.y = 108;
                            } else if (symbol.size === 2) {
                                textContainer.y = 170;
                            }
                        }

                        if (allWaysWinData.length === 0) {
                            allWaysWinData.push({
                                symbol: symbol as ApocalypseSymbol,
                                textContainers: [textContainer],
                            });
                        } else {
                            let symbolAlreadyExists = false;
                            allWaysWinData.forEach((wayData) => {
                                if (wayData.symbol === symbol) {
                                    wayData.textContainers.push(textContainer);
                                    symbolAlreadyExists = true;
                                }
                            });

                            if (!symbolAlreadyExists) {
                                allWaysWinData.push({
                                    symbol: symbol as ApocalypseSymbol,
                                    textContainers: [textContainer],
                                });
                            }
                        }

                        winScreen.addChild(textContainer);

                        const containerTl = gsap.timeline({
                            smoothChildTiming: true,
                            autoRemoveChildren: true,
                            onStart: () => {},
                            onComplete: () => {
                                textContainer.destroy();
                            },
                        });

                        const showContainerAnimation = gsap.to(textContainer, {
                            alpha: 1,
                            duration: 0.2,
                        });
                        const moveAndContainerAnimation = gsap.to(textContainer, {
                            y: `-=${symbolConfig.symbolHeight}`,
                            duration: 1,
                        });
                        const hideContainerAnimation = gsap.to(textContainer, {
                            alpha: 0,
                            duration: 0.3,
                        });

                        containerTl.add(showContainerAnimation, 0);
                        containerTl.add(moveAndContainerAnimation, 0.4);
                        containerTl.add(hideContainerAnimation, `>-0.3`);

                        symbolsAndWaysTimeline.add(containerTl, 0);
                    }
                });
            }
        });

        allWaysWinData.forEach((wayData) => {
            if (wayData.textContainers.length > 1) {
                const symbol = wayData.symbol;
                const isXNudge = symbol instanceof ApocalypseXNudgeSymbol;

                if (symbol.size === 1) {
                    wayData.textContainers.forEach((container, index) => {
                        container.pivot.set(0);

                        const valueText = container.getChildByName(`valueText`) as Text;
                        valueText.x = 0;
                        valueText.y = 0;

                        const wayText = container.getChildByName(`waysText`) as Text;
                        wayText.x = valueText.width + 2;
                        wayText.y = valueText.height / 2 - wayText.height / 2;

                        if (wayData.textContainers.length === 4) {
                            container.scale.set(0.8);
                        }
                        container.x = container.x - container.width / 2;
                        if ((symbol.parent.parent as ApocalypseReel).reelId === 5) {
                            if (container.x + container.width > 358) {
                                container.x = 358 - container.width;
                            }
                        }

                        const globalSymbolPosition = symbol.getGlobalPosition();
                        const localSymbolPosition = winScreen.toLocal(globalSymbolPosition);

                        let yOffset = 32;
                        let distanceBetweenSymbols = 20;
                        if (wayData.textContainers.length === 3) {
                            yOffset = isXNudge ? 17 : 20;
                            distanceBetweenSymbols = isXNudge ? 24 : 18;
                        } else if (wayData.textContainers.length === 4) {
                            yOffset = 15;
                            distanceBetweenSymbols = 14;
                        }

                        container.y =
                            localSymbolPosition.y +
                            yOffset +
                            (yOffset + distanceBetweenSymbols) * index -
                            container.height / 2;
                    });
                }

                if (symbol.size === 4) {
                    // ! vezi si daca size = 3 la nudge
                    wayData.textContainers.forEach((container, index) => {
                        container.x = isXNudge ? container.x : 0;

                        let yOffset = -66;
                        if (wayData.textContainers.length === 3) {
                            yOffset = -108;
                        } else if (wayData.textContainers.length === 4) {
                            yOffset = -128;
                        }
                        container.y = yOffset + (468 / wayData.textContainers.length) * index;
                    });
                } else if (symbol.size === 3) {
                    wayData.textContainers.forEach((container, index) => {
                        let yOffset = 16;
                        if (wayData.textContainers.length === 3) {
                            yOffset = -20;
                        } else if (wayData.textContainers.length === 4) {
                            yOffset = -36;
                        }
                        container.y = yOffset + (384 / wayData.textContainers.length) * index;
                    });
                } else if (symbol.size === 2) {
                    wayData.textContainers.forEach((container, index) => {
                        let yOffset = 112;
                        if (wayData.textContainers.length === 3) {
                            yOffset = 88;
                        } else if (wayData.textContainers.length === 4) {
                            yOffset = 75;
                        }
                        container.y = yOffset + (244 / wayData.textContainers.length) * index;
                    });
                }
            }
        });
    }

    /////////////

    timeline.add(symbolsAndWaysTimeline, TimelineLabels.START_SYMBOLS_WIN_ANIMATION);
    let labelTime: number = symbolsAndWaysTimeline.totalDuration() / 2;
    if (currentGameMode === AbstractGameMode.NORMAL) {
        timeline.addLabel(
            TimelineLabels.FINISH_SYMBOLS_WIN_ANIMATION,
            `>-=${symbolsAndWaysTimeline.totalDuration() / 2}`
        );
    } else {
        timeline.addLabel(
            TimelineLabels.FINISH_SYMBOLS_WIN_ANIMATION,
            TimelineLabels.TOTAL_WIN_DECORATOR_START
        );

        labelTime = symbolsAndWaysTimeline.totalDuration();
    }

    // end animate symbols

    const addSkip = () => {
        const skipToNextLevel = () => {
            const nextLabel: string = timeline.nextLabel();

            timeline.seek(timeline.labels[nextLabel], false);
        };

        data.flowStateController?.setSkipCallback(skipToNextLevel);
    };

    if (!winIsBelowStake) {
        if (winMultiplier < winLevels[minBigWinIndex].min) {
            switch (winType) {
                case WinType.SMALL:
                    winScreen.handleSmallWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                case WinType.MEDIUM:
                    winScreen.handleMediumWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                case WinType.LARGE:
                    winScreen.handleLargeWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                default:
                    winScreen.handleTinyWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });

                    break;
            }
        } else {
            winLevels.forEach((level: IWinLevel, index) => {
                if (index >= minBigWinIndex && winMultiplier >= level.min) {
                    const delay = currentGameMode === AbstractGameMode.NORMAL ? 0.4 : 0;
                    timeline.addLabel(
                        level.name,
                        `${TimelineLabels.TOTAL_WIN_DECORATOR_START}+=${labelTime + delay}`
                    );
                    labelTime += level.animationDuration;
                }
            });

            winLevels.forEach((level: IWinLevel, index) => {
                if (index >= minBigWinIndex && winMultiplier >= level.min) {
                    if (level.name === LevelName.MAX_CAP) {
                        winType = WinType.MAX_CAP;

                        winScreen.handleMaxCap({
                            timeline: timeline,
                            level: level,
                            animationDuration: labelTime,
                            callback: () => {
                                data.flowStateController?.setSkipCallback(undefined);
                            },
                            winAmount: winAmount,
                            winMultiplier: winMultiplier,
                            winType: WinType.MAX_CAP,
                            betAmount: betValue,
                            // waysWon: waysWon,
                        });
                    } else {
                        winScreen.handleBigWins({
                            timeline: timeline,
                            level: level,
                            finalLevel: winLevel,
                            animationDuration: labelTime,
                            callback: () => addSkip(),
                            winAmount: winAmount,
                            winMultiplier: winMultiplier,
                            winType: WinType.BIG,
                            betAmount: betValue,
                        });
                    }
                }
            });
        }

        switch (winType) {
            case WinType.BIG:
                timeline.addLabel(TimelineLabels.ON_BIG_WINS_COMPLETE_START);
                winScreen.onBigWinsComplete({
                    timeline: timeline,
                    winType: winType,
                    winAmount: winAmount,
                    level: winLevel,
                    waysWon: waysWon,
                    callback: () => {
                        if (
                            currentGameMode !== AbstractGameMode.NORMAL &&
                            winLevel.winType === WinType.MAX_CAP
                        ) {
                            addSkip();
                        } else if (currentGameMode === AbstractGameMode.NORMAL) {
                            addSkip();
                        }
                    },
                });

                timeline.addLabel(
                    TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE,
                    TimelineLabels.ON_BIG_WINS_COMPLETE_FINISH
                );
                break;

            case WinType.MAX_CAP: {
                timeline.addLabel(TimelineLabels.ON_MAX_CAP_COMPLETE_START);

                winScreen.onMaxCapComplete({ timeline: timeline, callback: () => addSkip() });

                timeline.call(
                    () => {
                        data.flowStateController?.setSkipCallback(undefined);
                    },
                    undefined,
                    `${TimelineLabels.ON_MAX_CAP_COMPLETE_START}-=0.1`
                );

                timeline.addLabel(
                    TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE,
                    TimelineLabels.ON_MAX_CAP_COMPLETE_FINISH
                );

                break;
            }
            default:
                timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE);
        }
    } else {
        timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE);
    }

    timeline.call(
        () => {
            onDecoratorComplete();

            if (!winIsBelowStake) {
                winScreen.finish({
                    winType: winType,
                    timeline: timeline,
                    finishTextAnimationDuration: finishTextAnimationDuration,
                    finishSpineAnimationDuration: finishSpineAnimationDuration,
                    waysWon: waysWon,
                });
            }
        },
        undefined,
        TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE
    );
};
